import { useState } from "react";
import { Input, Button, Select, Tooltip, Switch } from "antd";
import { NestedListEditor } from "./NestedListEditor";
import { CardIconGlyph } from "./CardIconGlyph";
import { IconPickerField } from "./IconPickerField";
import {
    resolveTranslatableValue,
    getEditableTranslatableValue,
    LANGUAGE_CODE_LABEL,
    TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE,
} from "./translatable.util";
import { WRAP_OPTIONS, resolveWrapDefault } from "./wrapControl.constants";
import { SECTION_ANCHOR_CUSTOM_VALUE } from "./sectionAnchors.constants";

const { TextArea } = Input;

// Default field definitions and new-item shape for OurAdvantage cards.
// Passed as defaults to CardsEditor so FAQ / future components can override.
// Step 11: `icon` migrated off a plain emoji text input onto the shared
// icon-library picker (type: "icon") — same control hero's features use.
// Existing published rows with a raw emoji string (e.g. "🚚") aren't
// forcibly rewritten: resolveIconComponent() returns null for any
// unrecognized name, so the picker just shows as unselected/default until
// an admin re-picks and republishes — no bulk migration, no crash.
const OUR_ADVANTAGE_CARD_FIELDS = [
    { field: "icon",        label: "Icon", type: "icon" },
    { field: "big",         label: "Stat number", translatable: true },
    { field: "unit",        label: "Unit",        translatable: true },
    { field: "title",       label: "Title",       translatable: true },
    { field: "description", label: "Description", translatable: true },
];
const OUR_ADVANTAGE_NEW_ITEM = { icon: "StarOutlined", big: "", unit: "", title: "New Advantage", description: "" };

// Label shown for items whose groupBy field resolves to an empty string
// (FAQ's accordion/grid layouts intentionally leave `category` blank) —
// these still need a home in the grouped UI instead of silently vanishing.
const UNGROUPED_LABEL = "Ungrouped";

// One card's header (collapse toggle + remove) and, when expanded, its field
// inputs. Extracted so both the flat list and the grouped-by-category list
// can render identical card bodies — `index` is always the card's real
// position in the original flat `cards` array (never the in-group position),
// so updateCard/removeCard's index-based lookups stay correct regardless of
// which rendering mode is active.
function CardItem({
    card,
    index,
    isExpanded,
    onToggle,
    onRemove,
    fields,
    selectedLanguage,
    componentKey,
    updateCard,
    updateTranslatableCard,
    headerText,
    hasIconField,
    isLocked,
}) {
    return (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 6, overflow: isExpanded ? "visible" : "hidden" }}>
            <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: "#fafafa", cursor: "pointer" }}
                onClick={onToggle}
            >
                <span style={{ fontSize: 12, color: "#444" }}>
                    {hasIconField && <CardIconGlyph icon={card.icon} />} {headerText}
                </span>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span style={{ fontSize: 10, color: "#bbb" }}>{isExpanded ? "▲" : "▼"}</span>
                    <Tooltip title={isLocked ? "This question cannot be deleted" : undefined}>
                        <Button
                            size="small"
                            danger
                            disabled={isLocked}
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            style={{ fontSize: 10, padding: "0 6px", height: 20 }}
                        >✕</Button>
                    </Tooltip>
                </div>
            </div>
            {isExpanded && (
                <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 5 }}>
                    {fields.map(({ field, label, translatable, type, subFields, subNewItemDefault, options, wrapControl, multiline, hint }) => {
                        if (type === "select") {
                            return (
                                <div key={field} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 11, color: "#888", width: 88, flexShrink: 0 }}>{label}</span>
                                    <Select
                                        size="small"
                                        style={{ flex: 1 }}
                                        value={card[field]}
                                        options={options}
                                        onChange={(val) => updateCard(index, field, val)}
                                    />
                                </div>
                            );
                        }
                        // A dropdown of this layout's known hideable sections
                        // (Quiz/FAQ) plus "Custom URL" — the underlying stored
                        // value is still just a plain href string (no schema
                        // change), so the dropdown's selection is derived by
                        // matching `card[field]` against each option's real
                        // `href` (see sectionAnchors.constants.js). Any href
                        // that doesn't match a registered section (including
                        // every href already saved before this picker
                        // existed) falls back to "Custom URL" mode, revealing
                        // a plain text input pre-filled with that value —
                        // nothing is ever silently overwritten.
                        if (type === "sectionAnchor") {
                            const matched = (options || []).find((o) => o.href === card[field]);
                            const selectedValue = matched ? matched.value : SECTION_ANCHOR_CUSTOM_VALUE;
                            return (
                                <div key={field} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 11, color: "#888", width: 88, flexShrink: 0 }}>{label}</span>
                                        <Select
                                            size="small"
                                            style={{ flex: 1 }}
                                            value={selectedValue}
                                            options={(options || []).map((o) => ({ value: o.value, label: o.label }))}
                                            onChange={(val) => {
                                                const opt = (options || []).find((o) => o.value === val);
                                                updateCard(index, field, opt && opt.href != null ? opt.href : "");
                                            }}
                                        />
                                    </div>
                                    {selectedValue === SECTION_ANCHOR_CUSTOM_VALUE && (
                                        <Input
                                            size="small"
                                            value={card[field] || ""}
                                            onChange={(e) => updateCard(index, field, e.target.value)}
                                            placeholder="https://... or /#anchor-id"
                                        />
                                    )}
                                </div>
                            );
                        }
                        if (type === "list") {
                            // subFields may be a plain array (every existing
                            // consumer) or a `(card) => fields[]` resolver, for
                            // consumers whose sub-field set depends on which
                            // card this is (e.g. quiz's calorie question
                            // showing a calorie-range control other questions
                            // don't need) — see quizLayouts.constants.js.
                            const resolvedSubFields = typeof subFields === "function" ? subFields(card) : subFields;
                            return (
                                <div key={field} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <span style={{ fontSize: 11, color: "#888" }}>{label}</span>
                                    <NestedListEditor
                                        items={card[field] || []}
                                        subFields={resolvedSubFields}
                                        subNewItemDefault={subNewItemDefault}
                                        onChange={(next) => updateCard(index, field, next)}
                                        selectedLanguage={selectedLanguage}
                                    />
                                </div>
                            );
                        }
                        if (type === "icon") {
                            return (
                                <div key={field} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 11, color: "#888", width: 88, flexShrink: 0 }}>{label}</span>
                                    <IconPickerField
                                        value={card[field]}
                                        onChange={(name) => updateCard(index, field, name)}
                                    />
                                </div>
                            );
                        }
                        if (type === "boolean") {
                            return (
                                <div key={field} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{ fontSize: 11, color: "#888", width: 88, flexShrink: 0 }}>{label}</span>
                                    <Switch checked={!!card[field]} onChange={(v) => updateCard(index, field, v)} size="small" />
                                </div>
                            );
                        }
                        // Multiline fields (FAQ's question/answer) stack the label
                        // above a full-width TextArea instead of the cramped
                        // label(88px) + inline-Input(flex) row every other field
                        // uses — that row was unreadable for paragraph-length text,
                        // wrapping only the label while the input scrolled horizontally.
                        if (multiline) {
                            return (
                                <div key={field} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 11, color: "#888" }}>
                                            {label}
                                            {translatable && ` (${LANGUAGE_CODE_LABEL[selectedLanguage]})`}
                                        </span>
                                        {wrapControl && (
                                            <Select
                                                size="small"
                                                value={card[`${field}Wrap`] ?? resolveWrapDefault(componentKey, field)}
                                                onChange={(v) => updateCard(index, `${field}Wrap`, v)}
                                                options={WRAP_OPTIONS}
                                                style={{ width: 110, flexShrink: 0 }}
                                            />
                                        )}
                                    </div>
                                    <TextArea
                                        autoSize={{ minRows: 2, maxRows: 6 }}
                                        value={translatable ? getEditableTranslatableValue(card[field], selectedLanguage) : (card[field] || "")}
                                        onChange={(e) => (translatable
                                            ? updateTranslatableCard(index, field, e.target.value)
                                            : updateCard(index, field, e.target.value))}
                                        placeholder={translatable
                                            ? TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE[selectedLanguage](label)
                                            : undefined}
                                        dir={translatable && selectedLanguage === "ar" ? "rtl" : "ltr"}
                                    />
                                </div>
                            );
                        }
                        // Fields with a `hint` (short input, but a long explanatory
                        // sentence — e.g. FAQ's "Category") stack the label above
                        // the input too, same reasoning as `multiline`: a long
                        // string in the fixed 88px label column either overflows
                        // or gets clipped unreadably. The hint itself renders as
                        // small text below the input, not inside the label.
                        if (hint) {
                            return (
                                <div key={field} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                        <span style={{ fontSize: 11, color: "#888" }}>
                                            {label}
                                            {translatable && ` (${LANGUAGE_CODE_LABEL[selectedLanguage]})`}
                                        </span>
                                        {wrapControl && (
                                            <Select
                                                size="small"
                                                value={card[`${field}Wrap`] ?? resolveWrapDefault(componentKey, field)}
                                                onChange={(v) => updateCard(index, `${field}Wrap`, v)}
                                                options={WRAP_OPTIONS}
                                                style={{ width: 110, flexShrink: 0 }}
                                            />
                                        )}
                                    </div>
                                    <Input
                                        size="small"
                                        value={translatable ? getEditableTranslatableValue(card[field], selectedLanguage) : (card[field] || "")}
                                        onChange={(e) => (translatable
                                            ? updateTranslatableCard(index, field, e.target.value)
                                            : updateCard(index, field, e.target.value))}
                                        placeholder={translatable
                                            ? TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE[selectedLanguage](label)
                                            : undefined}
                                        dir={translatable && selectedLanguage === "ar" ? "rtl" : "ltr"}
                                    />
                                    <span style={{ fontSize: 10, color: "#aaa" }}>{hint}</span>
                                </div>
                            );
                        }
                        return (
                            <div key={field} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 11, color: "#888", width: 88, flexShrink: 0 }}>
                                    {label}
                                    {translatable && ` (${LANGUAGE_CODE_LABEL[selectedLanguage]})`}
                                </span>
                                <Input
                                    size="small"
                                    value={translatable ? getEditableTranslatableValue(card[field], selectedLanguage) : (card[field] || "")}
                                    onChange={(e) => (translatable
                                        ? updateTranslatableCard(index, field, e.target.value)
                                        : updateCard(index, field, e.target.value))}
                                    placeholder={translatable
                                        ? TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE[selectedLanguage](label)
                                        : undefined}
                                    dir={translatable && selectedLanguage === "ar" ? "rtl" : "ltr"}
                                />
                                {wrapControl && (
                                    <Select
                                        size="small"
                                        value={card[`${field}Wrap`] ?? resolveWrapDefault(componentKey, field)}
                                        onChange={(v) => updateCard(index, `${field}Wrap`, v)}
                                        options={WRAP_OPTIONS}
                                        style={{ width: 110, flexShrink: 0 }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Generic inline CRUD editor for any `type: "cards"` schema field.
// `fields` controls which input rows appear; `newItemDefault` seeds the blank
// row added by "+ Add". Both default to OurAdvantage values so existing usage
// needs no change. FAQ and future components pass their own via the schema entry.
//
// Each `fields` entry may additionally set:
//   - `translatable: true` — the field's stored value becomes a per-language
//     object ({en, ru, ar}); this editor reads/writes value[selectedLanguage],
//     defaulting a legacy plain-string value to {en: value} on first edit.
//   - `type: "list"`, `subFields`, `subNewItemDefault` — the field's value is
//     itself an array of objects, rendered via NestedListEditor instead of a
//     plain Input.
// `selectedLanguage` is required whenever any field is `translatable`;
// defaults to "en" so existing (non-translatable) usage is unaffected.
//
// `groupBy` (optional, e.g. FAQ's "category"): when set, groups the flat
// `cards` array into collapsible sections keyed by that field's resolved
// value, each showing its item count — e.g. FAQ's 9 flat items become
// "Bonuses (3)" / "Products (3)" / "Payment (3)" instead of an undifferentiated
// "Card 1..9" list that gave no indication only one group's items show in the
// tabbed preview at a time.
function CardsEditor({
    cards,
    onChange,
    fields         = OUR_ADVANTAGE_CARD_FIELDS,
    newItemDefault = OUR_ADVANTAGE_NEW_ITEM,
    selectedLanguage = "en",
    componentKey   = "ourAdvantage",
    groupBy,
    nonDeletableIds = [],
    cardLabel      = "Card",
    // Navbar's navLinks is the one consumer whose items are meant to be a
    // fixed, curated set (matched against the sectionAnchor registry and
    // each brand's real page sections) rather than an open-ended list —
    // every other consumer (FAQ questions, ourAdvantage cards, social
    // links, etc.) keeps the "+ Add" button via this defaulting to true.
    allowAdd       = true,
    // Items from this layout's own default set that aren't currently in
    // `cards` (computed by SchemaFieldControl, which knows the current
    // layout) — each renders as its own "+ Restore X" button so an
    // accidentally-removed default link can come back exactly as it was,
    // without allowAdd reopening the door to arbitrary new links. Empty for
    // every consumer that doesn't pass `restoreCandidatesByLayout` on its
    // schema field, so this is a no-op everywhere except navbar's navLinks.
    restoreCandidates = [],
    onRestore,
}) {
    const [expanded, setExpanded] = useState(null);

    // `nonDeletableIds` may be a plain array of fixed id strings (every
    // existing consumer) OR a `(card) => boolean` predicate — quiz's calorie
    // question uses the predicate form (`card.questionType === "calorie"`)
    // because a single hardcoded id can't correctly identify "the calorie
    // question" across every brand's own quiz content: MEELZ's default seed
    // uses id "q5", but CK's real published content (QUIZ_CK_DEFAULT_PROPS)
    // uses "ckq3" for the exact same structural role — confirmed bug where
    // CK's calorie question was silently deletable (and E4F/MEELZ's wasn't)
    // because "q5" never matched CK's real id. Matching on the card's own
    // `questionType` field instead works correctly regardless of id, brand,
    // or question reordering.
    const isCardLocked = (card) =>
        typeof nonDeletableIds === "function" ? nonDeletableIds(card) : nonDeletableIds.includes(card.id);

    const updateCard = (index, field, val) => {
        onChange(cards.map((c, i) => (i === index ? { ...c, [field]: val } : c)));
    };

    const updateTranslatableCard = (index, field, val) => {
        onChange(cards.map((c, i) => {
            if (i !== index) return c;
            const existing = c[field];
            const asObject = existing == null || typeof existing === "string"
                ? (existing ? { en: existing } : {})
                : existing;
            return { ...c, [field]: { ...asObject, [selectedLanguage]: val } };
        }));
    };

    const addCard = (overrides) => {
        // A newItemDefault with `id: null` (e.g. quiz's questions, whose id
        // is a real cross-referenced key — see quizLayouts.constants.js) is
        // assigned a fresh unique id here instead of literally staying null,
        // which would collide across every newly-added card. Components
        // whose fields have no `id` concept (ourAdvantage/FAQ, addressed by
        // array index only) are unaffected — their newItemDefault has no
        // `id` key at all, so this is a no-op for them.
        const id = newItemDefault.id === null ? crypto.randomUUID() : undefined;
        onChange([...cards, { ...newItemDefault, ...(id ? { id } : {}), ...overrides }]);
        setExpanded(cards.length);
    };

    const removeCard = (index) => {
        // Defense-in-depth: the ✕ button is already disabled for locked
        // cards (see CardItem), but guard the actual mutation too in case
        // something else ever calls removeCard directly.
        if (cards[index] && isCardLocked(cards[index])) return;
        onChange(cards.filter((_, i) => i !== index));
        if (expanded === index) setExpanded(null);
        else if (expanded > index) setExpanded(expanded - 1);
    };

    // Collapsed-card header text: prefer whichever of "title"/"label" this
    // consumer's fields actually has (ourAdvantage/footer use `title`,
    // navbar's navLinks use `label`) — these are the two conventional
    // "display name" field names across every CardsEditor consumer. Falling
    // back to the first non-icon/non-list field would pick an arbitrary
    // field (e.g. ourAdvantage's numeric `big`) that isn't the card's name.
    // navbar's socialLinks has neither — falls back to its `select` field's
    // chosen option label (e.g. "Facebook") instead.
    const titleField = fields.find((f) => f.field === "title") || fields.find((f) => f.field === "label");
    const selectField = fields.find((f) => f.type === "select");
    const hasIconField = fields.some((f) => f.type === "icon");
    const cardHeaderText = (card) => {
        if (titleField) return resolveTranslatableValue(card[titleField.field], selectedLanguage);
        if (selectField) {
            const opt = selectField.options.find((o) => o.value === card[selectField.field]);
            return opt ? opt.label : "";
        }
        return "";
    };

    const cardItemProps = {
        fields,
        selectedLanguage,
        componentKey,
        updateCard,
        updateTranslatableCard,
        hasIconField,
    };

    if (groupBy) {
        // Group cards by the resolved (translation-aware) value of `groupBy`,
        // preserving each card's real index in the flat `cards` array so
        // updateCard/removeCard keep working unmodified. Group order follows
        // first-appearance order in `cards`, matching the tabbed preview's
        // own category-ordering logic.
        const groupOrder = [];
        const groupsByLabel = new Map();
        cards.forEach((card, i) => {
            const label = resolveTranslatableValue(card[groupBy], selectedLanguage) || UNGROUPED_LABEL;
            if (!groupsByLabel.has(label)) {
                groupsByLabel.set(label, []);
                groupOrder.push(label);
            }
            groupsByLabel.get(label).push({ card, index: i });
        });

        return (
            <div style={{ width: "100%", padding: "0 16px 8px" }}>
                {groupOrder.map((label) => {
                    const entries = groupsByLabel.get(label);
                    // Keyed by the group's first member's flat-array index, NOT
                    // the live label text: keying by `label` made React treat
                    // every keystroke inside that group's own Category input as
                    // "this group no longer exists, mount a new one" (the label
                    // changes on each character typed), unmounting the whole
                    // group subtree — including the very input being typed
                    // into — and dropping focus after one character. The first
                    // member's index is stable across re-renders regardless of
                    // what the category text currently says.
                    return (
                        <div key={entries[0].index} style={{ border: "1px solid #d9d9d9", borderRadius: 6, marginBottom: 8, background: "#f5f5f5" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px" }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>
                                    {label} ({entries.length})
                                </span>
                            </div>
                            <div style={{ padding: "0 8px 8px" }}>
                                {entries.map(({ card, index }) => (
                                    <CardItem
                                        key={index}
                                        card={card}
                                        index={index}
                                        isExpanded={expanded === index}
                                        onToggle={() => setExpanded(expanded === index ? null : index)}
                                        onRemove={() => removeCard(index)}
                                        headerText={cardHeaderText(card) || `Item ${entries.findIndex((e) => e.index === index) + 1}`}
                                        isLocked={isCardLocked(card)}
                                        {...cardItemProps}
                                    />
                                ))}
                                {allowAdd && (
                                    <Button
                                        size="small"
                                        onClick={() => addCard(label === UNGROUPED_LABEL ? {} : { [groupBy]: label })}
                                        style={{ width: "100%" }}
                                    >+ Add to {label}</Button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {allowAdd && (
                    <Button size="small" onClick={() => addCard()} style={{ width: "100%", marginTop: 2 }}>+ Add New Group / Item</Button>
                )}
            </div>
        );
    }

    return (
        <div style={{ width: "100%", padding: "0 16px 8px" }}>
            {cards.map((card, i) => (
                <CardItem
                    key={i}
                    card={card}
                    index={i}
                    isExpanded={expanded === i}
                    onToggle={() => setExpanded(expanded === i ? null : i)}
                    onRemove={() => removeCard(i)}
                    headerText={cardHeaderText(card) || `${cardLabel} ${i + 1}`}
                    isLocked={isCardLocked(card)}
                    {...cardItemProps}
                />
            ))}
            {allowAdd && (
                <Button size="small" onClick={() => addCard()} style={{ width: "100%", marginTop: 2 }}>+ Add {cardLabel}</Button>
            )}
            {restoreCandidates.map((candidate) => (
                <Button
                    key={candidate.href}
                    size="small"
                    type="dashed"
                    onClick={() => onRestore(candidate)}
                    style={{ width: "100%", marginTop: 4 }}
                >+ Restore {resolveTranslatableValue(candidate.label, selectedLanguage) || candidate.href}</Button>
            ))}
        </div>
    );
}

export { CardsEditor, OUR_ADVANTAGE_CARD_FIELDS, OUR_ADVANTAGE_NEW_ITEM };
